import React, { memo, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Collapse, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { useQuery } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import isEmpty from 'lodash/isEmpty'
import pickBy from 'lodash/pickBy'
import axios from 'axios'
import { cleanFilters, replaceValuesParams, validFinACarKeys } from 'utils/filters'
import { setFilters, setFindACarFilters, setPage } from 'redux/modules/search'
import usePageProps from 'hooks/usePageProps'
import EventEmitter from 'services/EventEmitter'
import BodyTypes from './Filters/BodyTypes'
import ShowMoreButton from './ShowMoreButton'
import PriceFilter from './Filters/PriceFilter'
import sx from './styles'
import StateFilter from './Filters/StateFilter'
import WarrantyFilter from './Filters/WarrantyFilter'
import PaymentMethodFilter from './Filters/PaymentMethodFilter'
import ActionButtons from './ActionButtons'
import SummaryFilter from './SummaryFilter'

const FindACar = ({
  onSubmit,
  onResetFindACarFilters,
}) => {
  const dispatch = useDispatch()
  const { bodiesType } = usePageProps()
  const [showMore, setShowMore] = useState(false)
  const [showSummary, setShowSummary] = useState(false)
  const [showAllFilters, setShowAllFilters] = useState(true)
  const filters = useSelector((s) => s.search.findACar)
  const customParams = { extense_info: true }
  const cFilters = replaceValuesParams(cleanFilters(filters))
  const params = { ...cFilters }

  const metaFilters = useQuery(
    ['metaFilters', filters],
    async () => {
      const urlWithOutFilter = `${process.env.NEXT_PUBLIC_API_ROOT}/api/filters?all_inventory=true`
      const urlWitFilter = `${process.env.NEXT_PUBLIC_API_ROOT}/api/filters`
      const url = isEmpty(pickBy(filters)) ? urlWithOutFilter : urlWitFilter
      const req = await axios.get(
        url,
        { params: { ...params, ...customParams } },
      )
      return req.data
    },
    {
      staleTime: 1000 * 60 * 24,
      keepPreviousData: true,
    },
  )

  const handleFiltersChange = (changedFilters) => {
    if (!showMore) {
      setShowMore(true)
    }
    dispatch(setFindACarFilters(changedFilters))
    dispatch(setPage(1))
  }

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'auto' })
  }

  const toggleShowMore = () => {
    setShowMore((prev) => !prev)
    scrollTop()
  }

  const toggleShowAllFilters = () => {
    setShowAllFilters((prev) => !prev)
    toggleShowMore()
  }

  const handleSubmit = () => {
    const filtersCleaned = pickBy(filters)
    dispatch(setFilters(filtersCleaned))
    scrollTop()
    setShowSummary(true)
    setShowAllFilters(false)
    toggleShowMore()
    setTimeout(() => {
      onSubmit(filtersCleaned)
    }, 500)
  }

  const handleResetFindACarFilters = () => {
    onResetFindACarFilters()
    setShowSummary(false)
    setShowAllFilters(true)
    setShowMore(true)
    scrollTop()
  }

  useEffect(() => {
    const cleanedObject = Object.keys(filters || {})
      ?.filter((name) => validFinACarKeys?.includes(name))
    if (!isEmpty(pickBy(cleanedObject))) {
      setShowSummary(true)
      setShowAllFilters(false)
      setShowMore(false)
    }
  }, [])

  const handleCloseFilters = () => {
    setShowAllFilters(false)
    setShowMore(false)
    setTimeout(() => {
      scrollTop()
    }, 500)
  }

  useEffect(() => {
    EventEmitter.suscribe('closeFilters', handleCloseFilters)
    return () => {
      EventEmitter.unsubscribe('closeFilters')
    }
  }, [])

  return (
    <Grid
      container
      sx={sx.root}
    >

      {!showSummary && (
        <Grid size={12}>
          <Typography
            variant="subtitle1"
            component="p"
          >
            <FormattedMessage id="SEARCH_FIND_A_CAR_TITLE" />
          </Typography>
        </Grid>
      )}

      {showSummary && (
        <Grid
          size={12}
          mb={1}
        >
          <SummaryFilter
            onResetFindACarFilters={handleResetFindACarFilters}
            toggleShowAllFilters={toggleShowAllFilters}
            showAllFilters={showAllFilters}
          />
        </Grid>
      )}

      {showAllFilters && (
        <Grid size={12}>
          <BodyTypes
            items={bodiesType}
            onChange={handleFiltersChange}
            bodiesSelected={filters?.body}
          />

          <Collapse
            in={showMore}
            collapsedSize={0}
          >

            <PriceFilter
              onChange={handleFiltersChange}
              minPriceSelected={filters?.min}
              maxPriceSelected={filters?.max}
            />

            <PaymentMethodFilter
              onChange={handleFiltersChange}
              hasCreditSelected={filters?.has_credit || ''}
              cashOnlySelected={filters?.cash_only || ''}
            />

            <StateFilter
              onChange={handleFiltersChange}
              branchSelected={filters?.branch_id}
            />

            <WarrantyFilter
              onChange={handleFiltersChange}
              hasWarrantySelected={filters?.has_warranty}
              priceNegotiableSelected={filters?.price_negotiable}
            />

            <ActionButtons
              onClearFilters={handleResetFindACarFilters}
              onSubmit={handleSubmit}
              totalVehicles={metaFilters?.data?.used}
            />

          </Collapse>

          {!showSummary && (
            <ShowMoreButton
              showMore={showMore}
              toggleShowMore={toggleShowMore}
            />
          )}

        </Grid>
      )}
    </Grid>
  )
}

export default memo(FindACar)
